/**
 * AdaptiveSidebar - Morphs between FilterPanel and ComparePanel
 *
 * Features:
 * - Smooth slide transitions between modes
 * - Persistent state across sessions
 * - Keyboard shortcuts (F for filters, C for compare)
 * - Collapsed state for detail views
 */

import { useEffect, useRef, useState } from 'preact/hooks'
import { appViewMode } from '../../state/appViewMode.js'
import { compareCells, panelMode } from '../../state/filterStore.js'
import { showCompareSidebar, sidebarVisible } from '../../state/globalSignals.js'
import FilterPanel from '../cells/FilterPanel.jsx'
import ComparePanel from '../compare/ComparePanel.jsx'
import { IconChevronLeft, IconChevronRight, IconCompare, IconSearch } from '../shared/Icons.jsx'

export default function AdaptiveSidebar({ collapsed = false }) {
  const mode = panelMode.value
  const compareCount = compareCells.value.length
  const isSingleCell = appViewMode.value === 'single-cell'

  // Auto-switch to compare mode in single-cell view
  useEffect(() => {
    if (isSingleCell && showCompareSidebar.value) {
      panelMode.value = 'compare'
      sidebarVisible.value = true
    }
  }, [isSingleCell, showCompareSidebar.value])
  const sidebarRef = useRef(null)
  const [sidebarHeight, setSidebarHeight] = useState('auto')

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.matches('input, textarea, select')) return

      // F = Filters
      if (e.key === 'f' || e.key === 'F') {
        e.preventDefault()
        panelMode.value = 'filter'
      }

      // C = Compare
      if (e.key === 'c' || e.key === 'C') {
        e.preventDefault()
        panelMode.value = 'compare'
      }

      // Escape = Toggle sidebar visibility
      if (e.key === 'Escape' && !collapsed) {
        sidebarVisible.value = !sidebarVisible.value
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [collapsed])

  // Auto-switch to compare when cells added (if not in filter mode)
  useEffect(() => {
    if (compareCount > 0 && mode === 'filter') {
      // Don't auto-switch, let user decide
    } else if (compareCount === 0 && mode === 'compare') {
      // Auto-switch back to filter when compare is empty
      panelMode.value = 'filter'
    }
  }, [compareCount, mode])

  // Sync sidebar height with content height
  useEffect(() => {
    const updateSidebarHeight = () => {
      const content = document.querySelector('.app-main-content')
      if (content && sidebarRef.current) {
        const contentHeight = content.offsetHeight // Visible height of content
        const maxHeight = window.innerHeight - 160 // Max: viewport - context(80) - footer(60) - gaps(20)
        // Match content height exactly (offsetHeight includes borders but not margins)
        const finalHeight = contentHeight
        sidebarRef.current.style.height = `${finalHeight}px`
      }
    }

    // Initial measurement
    updateSidebarHeight()

    // Listen to content resize (window resize, content changes)
    window.addEventListener('resize', updateSidebarHeight)

    // Use ResizeObserver for content changes
    const content = document.querySelector('.app-main-content')
    if (content) {
      const observer = new ResizeObserver(updateSidebarHeight)
      observer.observe(content)
      return () => {
        observer.disconnect()
        window.removeEventListener('resize', updateSidebarHeight)
      }
    }

    return () => window.removeEventListener('resize', updateSidebarHeight)
  }, [collapsed, mode])

  // Old collapsed prop is for single-cell view - sidebar completely hidden
  // Don't render anything if in single-cell collapsed mode
  if (collapsed) {
    return null
  }

  return (
    <div
      ref={sidebarRef}
      className={`adaptive-sidebar adaptive-sidebar--${mode === 'filter' ? 'filter' : 'compare'} ${!sidebarVisible.value ? 'adaptive-sidebar--collapsed' : ''} ${isSingleCell && showCompareSidebar.value ? 'show' : ''}`}
      data-mode={mode === 'filter' ? 'filter' : 'compare'}
      data-tour="welcome-sidebar"
    >
      {/* Collapsed State - Vertical Text + Icon */}
      {!sidebarVisible.value && (
        <div
          className="adaptive-sidebar__collapsed-content"
          onClick={() => sidebarVisible.value = true}
        >
          <button
            className="adaptive-sidebar__collapsed-icon"
            title="Expand Sidebar"
          >
            <IconChevronRight size={20} />
          </button>
          <div className="adaptive-sidebar__collapsed-text">
            {panelMode.value === 'compare' ? 'COMPARE' : 'FILTERS'}
          </div>
        </div>
      )}

      {/* Tabs - Always visible when expanded */}
      {sidebarVisible.value && (
        <div className="adaptive-sidebar__tabs">
            {/* Collapse Toggle Button - Links */}
            <button
              className="adaptive-sidebar__collapse-btn"
              onClick={(e) => {
                e.stopPropagation()
                sidebarVisible.value = false
              }}
              title="Collapse Sidebar"
            >
              <IconChevronLeft size={16} />
            </button>

            <button
              className={`adaptive-sidebar__tab ${mode === 'filter' ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                panelMode.value = 'filter'
              }}
              title="Filters (F)"
            >
              <span className="adaptive-sidebar__tab-icon">
                <IconSearch size={16} />
              </span>
              <span className="adaptive-sidebar__tab-label">Filters</span>
            </button>

            <button
              className={`adaptive-sidebar__tab ${mode === 'compare' ? 'active' : ''}`}
              onClick={(e) => {
                e.stopPropagation()
                panelMode.value = 'compare'
              }}
              title="Compare (C)"
            >
              <span className="adaptive-sidebar__tab-icon">
                <IconCompare size={16} />
              </span>
              <span className="adaptive-sidebar__tab-label">Compare</span>
              {compareCount > 0 && (
                <span className="adaptive-sidebar__tab-badge">{compareCount}</span>
              )}
            </button>
        </div>
      )}

      {/* Panel Content - Always rendered for smooth transitions and to prevent remounting */}
      <div className="adaptive-sidebar__content" style={{ display: !sidebarVisible.value ? 'none' : 'flex' }}>
        <div
          className={`adaptive-sidebar__panel adaptive-sidebar__panel--filter ${
            mode === 'filter' ? 'active' : 'inactive'
          }`}
        >
          <FilterPanel />
        </div>

        <div
          className={`adaptive-sidebar__panel adaptive-sidebar__panel--compare ${
            mode === 'compare' ? 'active' : 'inactive'
          }`}
        >
          <ComparePanel />
        </div>
      </div>


    </div>
  )
}
